<script lang="ts">
	// UC-12, settings, in four groups so an owner finds one without reading
	// them all:
	//
	//   This browser only  the dashboard's own language and theme. They apply
	//                      at once, with no Save, and change nothing else.
	//   The restaurant     its language, which receipts print in and every
	//                      screen starts in, and the dialling code.
	//   Tills              the idle lock and the quick cash amounts.
	//   Fixed              the currency and time zone, shown and changed
	//                      nowhere (plan, open question 1).
	//   Sign-in methods    the owner's ways in, counted here and added or
	//                      removed on their own page (D20, UC-28).
	//
	// Save sends the restaurant and the tills together.
	//
	// The quick cash amounts are the steps behind the till's quick cash
	// buttons, at most one per step and filled to three from the largest
	// (UX-21); the line under them shows what a cashier would see for a 65.00
	// total, so an owner can tell what the numbers do.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Printing from './Printing.svelte';
	import Field from '../components/Field.svelte';
	import Segmented from '../components/Segmented.svelte';
	import { api } from '../api/client';
	import { fill, languageOf, message, type Language } from '../i18n/messages';
	import { formatMinor } from '../format/money';
	import { suggestTenders } from '../domain/tender';
	import { loadSession } from '../owner/session.svelte';
	import { flash } from '../owner/notices.svelte';
	import { PriceInput } from '../owner/price.svelte';
	import { loadMethods, type SignInMethod } from '../owner/methods';
	import { navigate, SIGN_IN_METHODS_PATH } from '../router.svelte';
	import {
		languageChoice,
		setLanguageChoice,
		setThemeChoice,
		themeChoice,
		type ThemeChoice,
	} from '../display.svelte';
	import { untrack } from 'svelte';
	import type { components } from '../api/types';

	type Settings = components['schemas']['SettingsBody'];

	interface Props {
		language: Language;
		settings: Settings;
	}

	let { language, settings }: Props = $props();

	// How many ways in the owner has, and which: "Google and a password".
	let methods = $state<SignInMethod[] | null>(null);
	$effect(() => {
		void loadMethods().then((loaded) => (methods = loaded));
	});
	const methodsLine = $derived.by(() => {
		if (!methods || methods.length === 0) return '';
		const words = methods
			.map((m) =>
				message(language, m.provider === 'google' ? 'methodsGoogleWord' : 'methodsPasswordWord'),
			)
			.join(message(language, 'methodsAnd'));
		if (methods.length === 1) return fill(message(language, 'methodsCountOne'), words);
		if (methods.length === 2) return fill(message(language, 'methodsCountTwo'), words);
		return fill(message(language, 'methodsCountMany'), methods.length, words);
	});

	// The API's bounds.
	const MAX_STEPS = 8;
	const EXAMPLE_TOTAL = 6500;

	// A snapshot on purpose: the form holds what the owner is changing.
	const saved = untrack(() => settings);
	let chosenLanguage = $state<Language>(languageOf(saved.language));
	let countryCode = $state(saved.defaultCountryCode);
	let idleSeconds = $state(String(saved.idleLockSeconds));
	// Each amount is read as written, so 50 is 50.00 (owner/price.svelte.ts);
	// an empty one is left out when the settings are saved.
	function stepInput(minor: number | null): PriceInput {
		const step = new PriceInput(() => settings.minorDigits, { optional: true });
		step.load(minor);
		return step;
	}
	let steps = $state<PriceInput[]>((saved.quickTenderSteps ?? []).map(stepInput));
	const amounts = $derived(steps.map((step) => step.minor ?? 0).filter((minor) => minor > 0));
	let saving = $state(false);
	let failure = $state('');
	let printingOpen = $state(false);

	const languages = $derived([
		{
			value: 'ar',
			label: message(language, 'settingsLanguageArabic'),
			testId: 'browser-language-ar',
		},
		{
			value: 'msa',
			label: message(language, 'settingsLanguageMSA'),
			testId: 'browser-language-msa',
		},
		{
			value: 'en',
			label: message(language, 'settingsLanguageEnglish'),
			testId: 'browser-language-en',
		},
	]);
	const themes = $derived([
		{ value: 'light', label: message(language, 'themeLight'), testId: 'theme-light' },
		{ value: 'dark', label: message(language, 'themeDark'), testId: 'theme-dark' },
		{ value: 'system', label: message(language, 'themeSystem'), testId: 'theme-system' },
	]);

	const example = $derived(
		suggestTenders(EXAMPLE_TOTAL, amounts)
			.map((m) => formatMinor(m, settings.minorDigits))
			.join(' · '),
	);

	async function save() {
		if (saving) return;
		steps.forEach((step) => step.tidy());
		if (!steps.every((step) => step.valid)) return;
		saving = true;
		failure = '';
		try {
			// The page does not show the session length ("shift" names only the
			// cash shift), but the route still takes it at 1 to 24: send the
			// account's value as the server holds it now, read just before, so a
			// tab opened earlier never puts back an older one; no read, no save.
			const current = await api.GET('/api/owner/accounts/me');
			if (!current.data) {
				failure = current.error?.message ?? message(language, 'loadFailed');
				return;
			}
			const { error } = await api.PUT('/api/owner/settings', {
				body: {
					language: chosenLanguage,
					defaultCountryCode: countryCode.trim(),
					idleLockSeconds: Number(idleSeconds || '0'),
					sessionMaxHours: current.data.settings.sessionMaxHours,
					quickTenderSteps: amounts,
				},
			});
			if (error !== undefined) {
				failure = error.message ?? message(language, 'loadFailed');
				return;
			}
			flash(message(language, 'settingsSaved'));
			await loadSession();
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			saving = false;
		}
	}
</script>

<div class="settings" data-testid="panel-settings">
	<Card
		title={message(language, 'printingTitle')}
		note={message(language, 'printingSharedNote')}
		testId="settings-printing"
	>
		{#if printingOpen}
			<Printing {language} />
		{:else}
			<div class="browser">
				<Button
					testId="printing-open"
					label={message(language, 'printingOpenSettings')}
					tone="secondary"
					onPress={() => (printingOpen = true)}
				/>
			</div>
		{/if}
	</Card>
	{#if failure}
		<Band tone="flag" testId="settings-error">{failure}</Band>
	{/if}

	<Card
		title={message(language, 'settingsBrowserTitle')}
		note={message(language, 'settingsBrowserNote')}
		testId="settings-browser"
	>
		<div class="browser">
			<div class="pickers">
				<div class="group">
					<span class="label">{message(language, 'settingsLanguage')}</span>
					<Segmented
						label={message(language, 'settingsLanguage')}
						options={languages}
						value={languageChoice() ?? language}
						onChange={(picked) => setLanguageChoice(picked as Language)}
					/>
				</div>
				<div class="group">
					<span class="label">{message(language, 'settingsTheme')}</span>
					<Segmented
						label={message(language, 'settingsTheme')}
						options={themes}
						value={themeChoice()}
						onChange={(picked) => setThemeChoice(picked as ThemeChoice)}
					/>
				</div>
			</div>
			<p class="explain">{message(language, 'settingsBrowserExplain')}</p>
		</div>
	</Card>

	<Card
		title={message(language, 'settingsRestaurantTitle')}
		note={message(language, 'settingsRestaurantNote')}
	>
		<div class="grid">
			<div class="group">
				<span class="label">{message(language, 'settingsRestaurantLanguage')}</span>
				<Segmented
					label={message(language, 'settingsRestaurantLanguage')}
					options={[
						{
							value: 'ar',
							label: message(language, 'settingsLanguageArabic'),
							testId: 'settings-language-ar',
						},
						{
							value: 'msa',
							label: message(language, 'settingsLanguageMSA'),
							testId: 'settings-language-msa',
						},
						{
							value: 'en',
							label: message(language, 'settingsLanguageEnglish'),
							testId: 'settings-language-en',
						},
					]}
					value={chosenLanguage}
					onChange={(picked) => (chosenLanguage = picked as Language)}
				/>
				<span class="label">{message(language, 'settingsRestaurantLanguageNote')}</span>
			</div>
			<Field
				testId="settings-country-code"
				label={message(language, 'settingsCountryCode')}
				value={countryCode}
				inputMode="numeric"
				mono
				onInput={(v) => (countryCode = v.replace(/\D/g, '').slice(0, 3))}
			/>
		</div>
	</Card>

	<Card
		title={message(language, 'settingsTillsTitle')}
		note={message(language, 'settingsTillsNote')}
	>
		<div class="grid">
			<Field
				testId="settings-idle-seconds"
				label={message(language, 'settingsIdleSeconds')}
				note={fill(message(language, 'settingsIdleNote'), idleSeconds || 0)}
				value={idleSeconds}
				inputMode="numeric"
				mono
				onInput={(v) => (idleSeconds = v.replace(/\D/g, '').slice(0, 4))}
			/>
			<div class="wide group">
				<span class="label">{message(language, 'settingsQuick')}</span>
				<div class="steps">
					{#each steps as step, index (step)}
						<div class="step">
							<div class="amount">
								<Field
									testId="settings-step-{index}"
									ariaLabel={message(language, 'settingsAmount')}
									value={step.text}
									placeholder={formatMinor(0, settings.minorDigits)}
									error={step.error(language)}
									inputMode="decimal"
									mono
									onInput={step.set}
									onBlur={step.tidy}
								/>
							</div>
							<Button
								testId="settings-step-remove-{index}"
								label=""
								icon="close"
								ariaLabel={message(language, 'remove')}
								tone="text-quiet"
								size="sm"
								onPress={() => (steps = steps.filter((_, i) => i !== index))}
							/>
						</div>
					{/each}
					<Button
						testId="settings-step-add"
						icon="plus"
						label={message(language, 'settingsAmount')}
						tone="secondary"
						size="sm"
						disabled={steps.length >= MAX_STEPS}
						onPress={() => (steps = [...steps, stepInput(null)])}
					/>
				</div>
				<p class="example">
					{message(language, 'settingsExampleBefore')}
					<bdi class="n" dir="ltr">{formatMinor(EXAMPLE_TOTAL, settings.minorDigits)}</bdi>
					{message(language, 'settingsExampleAfter')}
					<bdi class="n strong" dir="ltr">{example}</bdi>
				</p>
			</div>
		</div>
	</Card>

	<div class="save">
		<Button
			testId="settings-save"
			label={message(language, 'save')}
			size="md"
			state={saving ? 'pending' : 'idle'}
			pendingLabel={message(language, 'saving')}
			onPress={() => void save()}
		/>
		<span class="label">{message(language, 'settingsSaveNote')}</span>
	</div>

	<Card
		title={message(language, 'settingsFixedTitle')}
		note={message(language, 'settingsFixedNote')}
	>
		<div class="fixed">
			<Field
				testId="settings-currency"
				label={message(language, 'settingsCurrency')}
				value="{settings.currencyCode} · {settings.minorDigits}"
				mono
			/>
			<Field
				testId="settings-timezone"
				label={message(language, 'settingsTimezone')}
				value={settings.timezone}
				mono
			/>
		</div>
	</Card>

	<Card title={message(language, 'methodsTitle')} note={message(language, 'methodsNote')}>
		<div class="ways">
			<span class="ways-count" data-testid="settings-methods-count">{methodsLine}</span>
			<Button
				testId="settings-methods"
				label={message(language, 'methodsTitle')}
				tone="secondary"
				size="sm"
				onPress={() => navigate(SIGN_IN_METHODS_PATH)}
			/>
		</div>
	</Card>
</div>

<style>
	.ways {
		padding: var(--card-pad);
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-4) var(--space-7);
	}

	.ways-count {
		flex: 1 1 16em;
		min-inline-size: 0;
		font-size: var(--text-base);
		color: var(--text-body);
	}

	.settings {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.browser {
		padding: var(--card-pad);
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.pickers {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-7) var(--space-11);
	}

	.explain {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}

	.grid {
		padding: var(--card-pad);
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
		gap: var(--space-9);
	}

	.group {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.wide {
		grid-column: 1 / -1;
	}

	.label {
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}

	.steps {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3);
		align-items: center;
	}

	.step {
		display: inline-flex;
		align-items: center;
		gap: var(--space-1);
	}

	.amount {
		inline-size: 120px;
	}

	.example {
		margin: 0;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.n {
		font-family: var(--font-mono);
	}

	.strong {
		font-weight: 600;
		color: var(--text-strong);
	}

	.save {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-5);
		align-items: center;
	}

	.save .label {
		flex: 1 1 16em;
		min-inline-size: 0;
	}

	.fixed {
		padding: var(--card-pad);
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
		gap: var(--space-7);
	}
</style>
